class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.initEventListeners();
    this.setHeaderCartIconAccessibility();
  }

  initEventListeners() {
    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.addEventListener('click', (event) => {
      const closeButton = event.target.closest('.btn-close, [data-cart-drawer-close]');
      if (!closeButton || !this.contains(closeButton)) return;

      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
  }

  getOffcanvasElement() {
    return this.querySelector('#CartDrawer');
  }

  getOffcanvasInstance(cartDrawer = this.getOffcanvasElement()) {
    if (!cartDrawer || typeof bootstrap === 'undefined' || !bootstrap.Offcanvas) return null;
    return bootstrap.Offcanvas.getInstance(cartDrawer) || new bootstrap.Offcanvas(cartDrawer, { backdrop: true, scroll: false });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const cartDrawer = this.getOffcanvasElement();
    if (!cartDrawer) return;

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const onShown = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : cartDrawer;
      const focusElement = this.querySelector('.drawer__inner');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    const instance = this.getOffcanvasInstance(cartDrawer);
    if (instance) {
      cartDrawer.addEventListener('shown.bs.offcanvas', onShown, { once: true });
      instance.show();
      return;
    }

    cartDrawer.classList.add('show');
    this.ensureBackdrop();
    cartDrawer.addEventListener(
      'transitionend',
      () => {
        onShown();
      },
      { once: true }
    );
  }

  ensureBackdrop() {
    if (document.querySelector('.offcanvas-backdrop, .cart-drawer-backdrop')) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'offcanvas-backdrop fade show';
    document.body.appendChild(backdrop);
    backdrop.addEventListener('click', () => this.close());
  }

  close() {
    const cartDrawer = this.getOffcanvasElement();
    if (!cartDrawer) return;

    const instance = typeof bootstrap !== 'undefined' && bootstrap.Offcanvas
      ? bootstrap.Offcanvas.getInstance(cartDrawer)
      : null;

    if (instance) {
      cartDrawer.addEventListener('hidden.bs.offcanvas', () => this.cleanupAfterClose(), { once: true });
      instance.hide();
      return;
    }

    cartDrawer.classList.remove('show', 'showing', 'hiding');
    this.cleanupAfterClose();
  }

  cleanupAfterClose() {
    removeTrapFocus(this.activeElement);

    document.querySelectorAll('.offcanvas-backdrop, .cart-drawer-backdrop').forEach((backdrop) => {
      backdrop.remove();
    });

    document.body.classList.remove('overflow-hidden', 'offcanvas-open', 'modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


function onKeyUpEscape(event) {
  if (event.code === 'Escape') {
    event.currentTarget.closest('details').removeAttribute('open');
  }
}


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.addEventListener('pageshow', (event) => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.close === 'function') {
    cartDrawer.close();
  }

  if (event.persisted) {
    const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      cartItems.onCartUpdate();
    }
  }
});
